import { useEffect, useRef, type ReactNode } from "react";

const CLOSE_DRAG_PX = 50;

interface TimelinePhoneSheetProps {
  onClose: () => void;
  /** Gets the place where the playback controls should be drawn. */
  onPlaybackHost: (host: HTMLElement | null) => void;
  children: ReactNode;
}

/** Everything that is not the playback bar, in one place a thumb can reach. */
export function TimelinePhoneSheet({ onClose, onPlaybackHost, children }: TimelinePhoneSheetProps) {
  const dragStart = useRef<number | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="timeline-sheet">
      <button
        type="button"
        className="timeline-sheet__backdrop"
        aria-label="Close player options"
        onClick={onClose}
      />
      <div className="timeline-sheet__panel" role="dialog" aria-label="Player options">
        <div
          className="timeline-sheet__grip"
          aria-hidden="true"
          onPointerDown={(event) => {
            dragStart.current = event.clientY;
          }}
          onPointerUp={(event) => {
            if (dragStart.current !== null && event.clientY - dragStart.current > CLOSE_DRAG_PX) {
              onClose();
            }
            dragStart.current = null;
          }}
        />
        <section aria-label="Playback">
          <h2 className="timeline-sheet__title">Playback</h2>
          <div ref={onPlaybackHost} />
        </section>
        {children}
        <button type="button" className="timeline-sheet__done" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
